import {
  Antenna,
  Bell,
  Clock,
  Globe,
  History,
  IdCard,
  KeyRound,
  Laptop,
  LogIn,
  LogOut,
  MessageSquare,
  MonitorSmartphone,
  Palette,
  ScanFace,
  ShieldCheck,
  SlidersHorizontal,
  SquareUser,
  UserRound,
  type LucideIcon,
} from 'lucide-react'
import { hrefDoPainel } from '@/components/AbasDeTela/navegacao'

/**
 * O MAPA de `/conta`, como DADO — molde de `admin/empresa/_lib/abasDaEmpresa` e
 * `admin/users/_lib/abasDoUsuario`. Nada aqui renderiza nem navega: quem desenha é o
 * `@/components/AbasDeTela` e quem traduz a URL é o `useNavegacaoNaUrl` dele.
 *
 * **O gate é da SEÇÃO Presença, nunca da aba** (D2): Sessões, Dispositivos, Redes e locais e
 * "Sair de todos os dispositivos" são de todo mundo, então Meus acessos não pode sumir com a
 * permission — some só a Presença, cujo hook (`useAuditSessions`) não tem `enabled`: fora do
 * mapa é a única coisa que impede o GET 403.
 */

export type AbaId = 'perfil' | 'historico' | 'meus-acessos' | 'seguranca' | 'preferencias'

/** Único no mapa inteiro: é a chave do slot e o valor de `?secao=`. Travado por teste. */
export type SecaoId =
  | 'dados'
  | 'avatar'
  | 'acesso'
  | 'escopos'
  | 'canais'
  | 'linha-do-tempo'
  | 'sessoes'
  | 'dispositivos'
  | 'redes-e-locais'
  | 'presenca'
  | 'sair-de-todos'
  | 'senha'
  | 'aparencia'
  | 'chat'
  | 'notificacoes'

/** Sem terceiro nível nesta tela. */
export type PainelId = SecaoId

export interface GatesDaConta {
  /** `audit:read`, resolvido pela página com `!isRevalidating` (I7). */
  podeVerAuditoria: boolean
}

export interface SecaoDef {
  id: SecaoId
  rotulo: string
  icone: LucideIcon
  /** Gate da seção. Ausente = sempre visível. Chave do gate, não literal de permission: quem lê grant é a página. */
  requer?: keyof GatesDaConta
}

export interface AbaDef {
  id: AbaId
  rotulo: string
  icone: LucideIcon
  secoes: readonly SecaoDef[]
}

export const ROTA_DA_CONTA = '/conta'
export const ABA_PADRAO: AbaId = 'perfil'

export const ABAS: readonly AbaDef[] = [
  {
    id: 'perfil',
    rotulo: 'Perfil',
    icone: UserRound,
    secoes: [
      { id: 'dados', rotulo: 'Dados pessoais', icone: IdCard },
      { id: 'avatar', rotulo: 'Avatar', icone: SquareUser },
      { id: 'acesso', rotulo: 'Acesso', icone: ShieldCheck },
      { id: 'escopos', rotulo: 'Escopos', icone: ScanFace },
      { id: 'canais', rotulo: 'Canais', icone: Antenna },
    ],
  },
  {
    // A trilha do PRÓPRIO usuário (`/users/me/events`): sem gate — `users:history:read` é pra ler a dos outros.
    id: 'historico',
    rotulo: 'Histórico',
    icone: History,
    secoes: [{ id: 'linha-do-tempo', rotulo: 'Linha do tempo', icone: History }],
  },
  {
    id: 'meus-acessos',
    rotulo: 'Meus acessos',
    icone: MonitorSmartphone,
    secoes: [
      { id: 'sessoes', rotulo: 'Sessões', icone: LogIn },
      { id: 'dispositivos', rotulo: 'Dispositivos', icone: Laptop },
      { id: 'redes-e-locais', rotulo: 'Redes e locais', icone: Globe },
      { id: 'presenca', rotulo: 'Presença', icone: Clock, requer: 'podeVerAuditoria' },
      { id: 'sair-de-todos', rotulo: 'Sair de todos os dispositivos', icone: LogOut },
    ],
  },
  {
    id: 'seguranca',
    rotulo: 'Segurança',
    icone: KeyRound,
    secoes: [{ id: 'senha', rotulo: 'Senha', icone: KeyRound }],
  },
  {
    id: 'preferencias',
    rotulo: 'Preferências',
    icone: SlidersHorizontal,
    secoes: [
      { id: 'aparencia', rotulo: 'Aparência', icone: Palette },
      { id: 'chat', rotulo: 'Chat', icone: MessageSquare },
      { id: 'notificacoes', rotulo: 'Notificações', icone: Bell },
    ],
  },
] as const

/**
 * O mapa recortado: seção gateada NÃO entra (some da sidebar e, vinda na URL, cai como id
 * desconhecido → primeiro painel). Ausência é ausência, nunca `hidden` (R04). A página memoiza.
 */
export function abasVisiveis(gates: GatesDaConta): AbaDef[] {
  return ABAS.map((aba) => ({ ...aba, secoes: aba.secoes.filter((s) => !s.requer || gates[s.requer]) }))
}

/** Link colável de uma seção — tipado pelo mapa: redirect, rail e notificação de teste não apontam pra seção que não existe. */
export function hrefDaSecao(secao: SecaoId): string {
  return hrefDoPainel(ROTA_DA_CONTA, secao)
}
